import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import { Progress } from "../ui/progress";
import { Spinner } from "../ui/spinner";
import {
  Database,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Shield,
  Folder,
  Clock,
} from "lucide-react";
import { MigrationService } from "../../services/migrationService";

const MigrationManagement = ({ organizationId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [migrationData, setMigrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    loadMigrationData();
  }, [organizationId]);

  const loadMigrationData = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await MigrationService.getControlsNeedingMigration(
        organizationId,
        getAccessTokenSilently
      );

      setMigrationData(data);
    } catch (err) {
      setError(err.message);
      console.error("Failed to load migration data:", err);
    } finally {
      setLoading(false);
    }
  };

  const performMigration = async () => {
    try {
      setMigrating(true);
      setError("");

      const results = await MigrationService.migrateControlCategories(
        {
          organization_id: organizationId,
          // Could add additional migration options here
        },
        getAccessTokenSilently
      );

      setMigrationResults(results);
      await loadMigrationData(); // Refresh data
    } catch (err) {
      setError(err.message);
      console.error("Migration failed:", err);
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center p-8">
          <Spinner size="lg" />
        </CardContent>
      </Card>
    );
  }

  if (error && !migrationData) {
    return (
      <Card>
        <CardContent className="p-8">
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-red-800">
              Failed to load migration data: {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!migrationData?.controls_needing_migration?.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-8">
          <CheckCircle className="h-12 w-12 text-green-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Migration Complete</h3>
          <p className="text-muted-foreground text-center">
            All controls have been successfully migrated to the new sub-category
            system.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            Control Migration to Sub-Categories
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Migration Status */}
          <Alert>
            <Clock className="h-4 w-4" />
            <div>
              <h4 className="font-medium">Migration Required</h4>
              <p className="text-sm text-muted-foreground mt-1">
                {migrationData.controls_needing_migration.length} controls need
                to be migrated to the new sub-category system.
              </p>
            </div>
          </Alert>

          {/* Migration Preview */}
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Migration Preview
            </h4>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {migrationData.controls_needing_migration
                .slice(0, 10)
                .map((control) => (
                  <div
                    key={control.id}
                    className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {control.custom_id && (
                          <Badge
                            variant="outline"
                            className="font-mono text-xs"
                          >
                            {control.custom_id}
                          </Badge>
                        )}
                        <span className="font-medium">{control.name}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {control.description && (
                          <p className="mb-1">{control.description}</p>
                        )}
                        <p>
                          Current: {control.categories?.length || 0} categories
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="text-sm text-green-700">
                      Will be assigned to appropriate sub-category
                    </div>
                  </div>
                ))}

              {migrationData.controls_needing_migration.length > 10 && (
                <div className="text-center py-2 text-sm text-muted-foreground bg-gray-50 rounded border">
                  And {migrationData.controls_needing_migration.length - 10}{" "}
                  more controls...
                </div>
              )}
            </div>
          </div>

          {/* Available Sub-Categories */}
          {migrationData.available_sub_categories &&
            migrationData.available_sub_categories.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  Available Sub-Categories
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {migrationData.available_sub_categories.map((subCategory) => (
                    <div
                      key={subCategory.id}
                      className="p-2 border rounded bg-green-50"
                    >
                      <div className="flex items-center gap-2">
                        <Shield className="h-3 w-3 text-green-600" />
                        <span className="text-sm font-medium">
                          {subCategory.name}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {subCategory.id_prefix}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {subCategory.Category?.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Migration Actions */}
          <div className="flex flex-col gap-4">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={performMigration}
              disabled={migrating}
              className="w-full flex items-center gap-2"
              size="lg"
            >
              {migrating ? (
                <>
                  <Spinner size="sm" />
                  Migrating Controls...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4" />
                  Start Migration
                </>
              )}
            </Button>

            <div className="text-xs text-muted-foreground text-center">
              <p>⚠️ This operation will:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Assign controls to appropriate sub-categories</li>
                <li>Generate custom IDs for controls that don't have them</li>
                <li>Preserve existing control data and associations</li>
                <li>Cannot be undone - please ensure you have backups</li>
              </ul>
            </div>
          </div>

          {/* Migration Results */}
          {migrationResults && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <div>
                <h4 className="font-medium text-green-800">
                  Migration Completed Successfully!
                </h4>
                <div className="text-sm text-green-700 mt-1 space-y-1">
                  <p>✅ {migrationResults.migrated_count} controls migrated</p>
                  {migrationResults.custom_ids_generated && (
                    <p>
                      ✅ {migrationResults.custom_ids_generated} custom IDs
                      generated
                    </p>
                  )}
                  {migrationResults.errors?.length > 0 && (
                    <p>
                      ⚠️ {migrationResults.errors.length} controls had issues
                    </p>
                  )}
                </div>
              </div>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MigrationManagement;
